import {
    Breakpoint,
    CandidateAddEditInterviewDialog,
    CandidateInterviewResult,
    ConfirmationDialog,
    FeedbackDetails,
    FeedbackModal,
    FeedbackTypeEnum,
    RejectCandidate,
    RejectCandidateDialog,
    SetInterviewSubmitStatus,
    useNotifications,
} from "@relode/core";
import { JobSalaryType } from "@relode/core/domain";
import * as React from "react";

import {
    HiredCandidateDialog,
    SetHiredCandidateStatus,
} from "../../../../../shared/components/candidate";
import {
    HiredCandidate,
    HiredCandidateToUpdate,
} from "../../../../../shared/types";
import { useCandidateContext } from "../../../_infra";
import { CandidateActionsDesktop } from "./candidateActionsDesktop";
import { CandidateActionsMobile } from "./candidateActionsMobile";
import { CandidateActionsProps } from "./candidateActionsProps";

const NOT_SHARED_JOB_MESSAGE =
    "You are not shared to this job. You must be shared to this job to give feedback.";
const JOB_NOT_CORRECT_STATUS_MESSAGE =
    "Feedback can only be given when a candidate is in Submitted or Interviewing status.";

export const CandidateActions = (props: {
    addOrUpdateFeedback: (comment: string, vote: boolean) => void;
    feedbackDetails: FeedbackDetails;
}) => {
    const { addOrUpdateFeedback, feedbackDetails } = props;

    const {
        state: {
            data: { candidate },
            ui: { isFeedbackUpdating },
        },
        actions: { reject, interview, offer, hire, updateHiredEndDate },
    } = useCandidateContext();

    const { actions: notifications } = useNotifications();
    const [
        isFeedbackModalOpen,
        setIsFeedbackModalOpen,
    ] = React.useState<boolean>(false);

    const [selectedVote, setSelectedVote] = React.useState<FeedbackTypeEnum>();

    const [interviewModalIsOpen, setInterviewModalOpen] = React.useState(false);

    const [isHireCandidateOpen, setIsHireCandidateOpen] = React.useState(false);

    const [
        offeredConfirmationModalIsOpen,
        setOfferedConfirmationModalOpen,
    ] = React.useState(false);

    const [rejectModalIsOpen, setRejectModalOpen] = React.useState(() => false);

    const { actions } = candidate;

    const canViewReject =
        candidate.canViewCandidateActions &&
        !!actions.negativeCandidateStatusId &&
        !!actions.negativeCandidateStatusActionName;

    const canViewPositiveAction =
        candidate.canViewCandidateActions &&
        !!actions.positiveCandidateStatusActionName &&
        !!actions.positiveCandidateStatusId;

    React.useEffect(() => {
        if (feedbackDetails && !!feedbackDetails.clientFeedback) {
            setSelectedVote(
                feedbackDetails.clientFeedback.vote
                    ? FeedbackTypeEnum.Positive
                    : FeedbackTypeEnum.Negative
            );
        }
    }, [feedbackDetails]);

    const handleFeedbackClick = (vote: FeedbackTypeEnum) => {
        if (!candidate.feedbackDetails.isOnJob) {
            notifications.showModal(
                "info",
                `Unable to give feedback for ${candidate.highlights.fullName}`,
                NOT_SHARED_JOB_MESSAGE
            );
        } else if (!candidate.feedbackDetails.canGiveFeedback) {
            notifications.showModal(
                "info",
                `Unable to give feedback for ${candidate.highlights.fullName}`,
                JOB_NOT_CORRECT_STATUS_MESSAGE
            );
        } else {
            setSelectedVote(vote);
            setIsFeedbackModalOpen(true);
        }
    };

    const handleConfirmFeedback = (comment: string, vote: boolean) => {
        if (
            comment !== feedbackDetails?.clientFeedback?.comment ||
            vote !== feedbackDetails?.clientFeedback?.vote && 
            !isFeedbackUpdating
        ) {
            addOrUpdateFeedback(comment, vote);
            setSelectedVote(
                vote ? FeedbackTypeEnum.Positive : FeedbackTypeEnum.Negative
            );
        }
        setIsFeedbackModalOpen(false);
    };

    const onFeedbackCancel = () => {
        setIsFeedbackModalOpen(false);
        if (feedbackDetails && !!feedbackDetails.clientFeedback) {
            setSelectedVote(
                feedbackDetails.clientFeedback.vote
                    ? FeedbackTypeEnum.Positive
                    : FeedbackTypeEnum.Negative
            );
        } else {
            setSelectedVote(undefined);
        }
    };

    const handleRejectClick = () => {
        if (candidate.canReject) {
            setRejectModalOpen(true);
        } else {
            notifications.showModal(
                "error",
                "Interviews in scheduled status",
                "Candidate cannot be rejected when there are scheduled interviews. " +
                    "Please update them to completed or canceled."
            );
        }
    };

    const handleRejectionComplete = async (rejectResponse: RejectCandidate) => {
        await reject(
            candidate.highlights.leadId,
            candidate.job.jobId,
            rejectResponse.reason,
            rejectResponse.details,
            rejectResponse.hiredEndDate,
            candidate.candidateConcurrencyToken
        );
        setRejectModalOpen(false);
    };

    const handleCandidateUpdateHiredEndDateComplete = async (
        hiredEndDate: Date
    ) => {
        const candidateHireDetailsToUpdate: HiredCandidate = {
            hiredStartDate: candidate.hiredDetails.startDate,
            hiredEndDate: hiredEndDate,
            hiredHoursPerWeek: candidate.hiredDetails.hoursPerWeek,
            hiredJobSalaryInUSDollars: candidate.hiredDetails.salaryInUSDollars,
            hiredJobSalaryTypeId: candidate.hiredDetails.salaryTypeId,
            candidateConcurrencyToken: candidate.candidateConcurrencyToken,
        };

        await updateHiredEndDate(
            candidate.job.jobId,
            candidate.highlights.leadId,
            candidateHireDetailsToUpdate
        );
        setRejectModalOpen(false);
    };

    const handlePositiveActionClick = (newCandidateStatus: string) => {
        switch (newCandidateStatus) {
            case "interview": {
                setInterviewModalOpen(true);
                break;
            }
            case "offer": {
                if (candidate.canOffer) {
                    setOfferedConfirmationModalOpen(true);
                } else {
                    notifications.showModal(
                        "error",
                        "Interviews in scheduled status",
                        "All interviews must be in completed or cancelled status to move a candidate to offered."
                    );
                }
                break;
            }
            case "hire": {
                setIsHireCandidateOpen(true);
                break;
            }
            default: {
                throw new Error(
                    `candidate status type unknown: ${newCandidateStatus}`
                );
            }
        }
    };

    const handleSubmitInterview = (
        interviewResult: CandidateInterviewResult,
        setInterviewSubmitStatus: SetInterviewSubmitStatus
    ) =>
        interview(
            candidate.highlights.leadId,
            candidate.job.jobId,
            interviewResult,
            candidate.candidateConcurrencyToken,
            setInterviewSubmitStatus
        );

    const handleOfferedComplete = async () => {
        await offer(
            candidate.highlights.leadId,
            candidate.job.jobId,
            candidate.candidateConcurrencyToken
        );
        setOfferedConfirmationModalOpen(false);
    };

    const handleSubmitHire = async (
        hiredCandidate: HiredCandidateToUpdate,
        setHiredCandidateStatus: SetHiredCandidateStatus
    ) => {
        await hire(
            candidate.job.jobId,
            candidate.highlights.leadId,
            hiredCandidate,
            setHiredCandidateStatus
        );
    };

    const getDefaultHiredCandidate = (): HiredCandidate => {
        const {
            hiredDetails: {
                salaryTypeId,
                salaryInUSDollars,
                startDate,
                endDate,
            },
            job: {
                jobType,
                billRateInUSDollars,
                jobSalary,
                jobSalaryType,
                contractEndDate,
                contractStartDate,
            },
        } = candidate;

        const jobHoursPerWeek = candidate.job.hoursPerWeek;
        const candidateHiredHoursPerWeek = candidate.hiredDetails.hoursPerWeek;

        const defaultStartDate =
            startDate ||
            (jobType.isContractOrLocum() ? contractStartDate : new Date());

        const defaultEndDate =
            endDate || jobType.isContractOrLocum() ? contractEndDate : null;

        const defaultHoursPerWeek =
            candidateHiredHoursPerWeek || jobHoursPerWeek;

        return {
            hiredJobSalaryInUSDollars:
                salaryInUSDollars ||
                (jobType.isContractOrLocum() ? billRateInUSDollars : jobSalary),
            hiredJobSalaryTypeId:
                salaryTypeId ||
                (jobType.isContractOrLocum()
                    ? JobSalaryType.PerHour.id
                    : jobSalaryType.id),
            candidateConcurrencyToken: candidate.candidateConcurrencyToken,
            hiredStartDate: defaultStartDate,
            hiredEndDate: defaultEndDate,
            hiredHoursPerWeek: defaultHoursPerWeek,
        };
    };

    const innerProps: CandidateActionsProps = {
        feedbackDetails,
        onFeedbackClick: handleFeedbackClick,
        selectedVote,
        canViewReject,
        canViewPositiveAction,
        onRejectClick: handleRejectClick,
        onPositiveActionClick: handlePositiveActionClick,
    };

    return (
        <>
            <Breakpoint mobile>
                <CandidateActionsMobile {...innerProps} />
            </Breakpoint>
            <Breakpoint desktop>
                <CandidateActionsDesktop {...innerProps} />
            </Breakpoint>

            {rejectModalIsOpen ? (
                <RejectCandidateDialog
                    candidate={{
                        candidateFullName: candidate.highlights.fullName,
                        dnr: null,
                        showDnr: false,
                        hiredEndDate: !!candidate.hiredDetails.endDate
                            ? candidate.hiredDetails.endDate
                            : new Date(),
                        hiredStartDate: candidate.highlights.startDate,
                        showHiredEndDate:
                            candidate.job.jobType.isFullTimeOrPartTime() &&
                            !!candidate?.hiredDetails.startDate,
                        isNoShowRejectionReasonEligible:
                            candidate.status.isNoShowRejectionReasonEligible,
                        isCandidateRejectPromptRequired:
                            candidate.isCandidateRejectPromptRequired,
                    }}
                    showInternalDetailsMessage={false}
                    onRejectCandidate={handleRejectionComplete}
                    onSetHiredEndDateForCandidate={
                        handleCandidateUpdateHiredEndDateComplete
                    }
                    onCancel={() => setRejectModalOpen(false)}
                />
            ) : null}

            {interviewModalIsOpen ? (
                <CandidateAddEditInterviewDialog
                    onCancel={() => setInterviewModalOpen(false)}
                    onComplete={() => setInterviewModalOpen(false)}
                    onSubmit={handleSubmitInterview}
                    candidateInterview={{
                        candidateFullName: candidate.highlights.fullName,
                    }}
                />
            ) : null}

            <ConfirmationDialog
                open={offeredConfirmationModalIsOpen}
                title="confirm move to offered"
                message="Are you sure you want to move this candidate to offered status?"
                onConfirm={handleOfferedComplete}
                onClose={() => setOfferedConfirmationModalOpen(false)}
            />

            {isHireCandidateOpen ? (
                <HiredCandidateDialog
                    candidateName={candidate.highlights.fullName}
                    candidate={getDefaultHiredCandidate()}
                    job={candidate.job}
                    onComplete={() => setIsHireCandidateOpen(false)}
                    onClose={() => setIsHireCandidateOpen(false)}
                    onSubmit={handleSubmitHire}
                />
            ) : null}
            {feedbackDetails.canGiveFeedback && isFeedbackModalOpen ? (
                <FeedbackModal
                    candidateFullName={candidate.highlights.fullName}
                    onCancel={onFeedbackCancel}
                    onConfirm={handleConfirmFeedback}
                    selectedFeedbackType={selectedVote}
                    comment={feedbackDetails.clientFeedback?.comment}
                    isLoading={isFeedbackUpdating}
                />
            ) : null}
        </>
    );
};
